/**
 * Why a result row is on screen, and which of its words to light.
 *
 * A result list has one job before it has any other: answer **"why is this row
 * here"**. A shop types `ramesh`, gets back a row titled "IPhone", and without
 * an answer that reads as a defect. It is there because the sale points at
 * Ramesh, and his name lives on his own row.
 *
 * Two devices, answering two different questions:
 *
 *   the highlight     WHAT matched, lit in the words themselves
 *   the field label   WHERE it matched, when that is not already obvious
 *
 * The label is left off a title match on purpose. The first line is already the
 * answer, so a label there would only repeat it. Label the surprise.
 *
 * **Neither may guess.** Two rules follow from that, and both cost real work:
 *
 * 1. The field is found by asking `fieldWords` — the same function that put the
 *    words in the index. So the field named is guaranteed to be one that
 *    actually contributed to the match. It is not a second, looser opinion
 *    about what matched.
 *
 * 2. The highlight is NOT a substring search. The tokenizer folds accents and
 *    case in order to match, so `chavan` matches `Chàvan` — and a plain
 *    `indexOf('chavan')` finds nothing there and lights nothing, on a row that
 *    certainly did match. Both sides go through the same fold. Where a fold
 *    still cannot be located, the row draws with no marks.
 *
 * A missing highlight is a blemish. A wrong one is a lie.
 *
 * Pure, so it is tested under bun with no database.
 */
import type { EntryValues, RecordSchemaField, RecordSchemaResponse } from '@alluzo/shared';

import { fieldWords, ownNameField, POINTS_AT_AN_ENTRY } from '@/lib/search-doc';
import { wordsIn } from '@/lib/search-query';

/**
 * Where a match landed.
 *
 * `title` carries no field, because a title match draws no label. `reference`
 * carries the id of the entry that actually holds the matching words, so the
 * screen can look up its name.
 */
export type MatchWhere =
  | { readonly kind: 'title' }
  | { readonly kind: 'field'; readonly field: RecordSchemaField }
  | { readonly kind: 'reference'; readonly field: RecordSchemaField; readonly targetId: string };

/** One run of a value, lit or not. What the row draws. */
export type Segment = { readonly text: string; readonly lit: boolean };

/**
 * Lowercase, and accents removed.
 *
 * The same fold `unicode61 remove_diacritics 2` applies, for the Latin range
 * that matters here: `Chàvan` and `chavan` become one string, so a term the
 * index matched through the fold can still be located in the drawn value.
 *
 * The combining-mark range is written out rather than using a unicode property
 * escape, so this behaves identically on Hermes and under bun.
 */
export const fold = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/** What the tokenizer treats as a break between terms. */
const BREAKS = ' \t\n\r\f\v!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~';

const DIGITS = '0123456789';
const isDigit = (one: string | undefined): boolean => one !== undefined && DIGITS.includes(one);

/**
 * Fold a value, keeping a way back to where each character came from.
 *
 * Two things make this more than a `replace`.
 *
 * **Code points, not UTF-16 units.** An emoji is one character and two units, so
 * a position found in a folded STRING cannot index the original. Everything here
 * is an array of code points, on both sides, and `from` says which original
 * character produced each folded one.
 *
 * **A comma inside a number is not a break.** The index holds an amount as its
 * raw digits, and the box strips the commas out of what is typed. So the DRAWN
 * value `1,023` has to fold to `1023` as well, or the run could never be found
 * and an amount match would light nothing.
 *
 * `dropped` names the characters that produced no folded output. They are the
 * gaps a lit run may need to close over.
 */
const foldWithMap = (
  chars: readonly string[],
): { folded: string[]; from: number[]; dropped: Set<number> } => {
  const folded: string[] = [];
  const from: number[] = [];
  const dropped = new Set<number>();
  for (let at = 0; at < chars.length; at += 1) {
    const one = chars[at] ?? '';
    if (one === ',' && isDigit(chars[at - 1]) && isDigit(chars[at + 1])) {
      dropped.add(at);
      continue;
    }
    const parts = [...fold(one)];
    if (parts.length === 0) {
      dropped.add(at);
      continue;
    }
    for (const part of parts) {
      folded.push(part);
      from.push(at);
    }
  }
  return { folded, from, dropped };
};

/**
 * Every position where `needle` STARTS A TERM in `folded`.
 *
 * Starting a term is the whole point: a prefix query matches only from the
 * beginning of a term, so `ram` must never light the middle of "program". That
 * would claim a match FTS5 did not make.
 */
const startsAt = (folded: readonly string[], needle: readonly string[]): number[] => {
  const hits: number[] = [];
  if (needle.length === 0) return hits;
  for (let at = 0; at + needle.length <= folded.length; at += 1) {
    if (at !== 0 && !BREAKS.includes(folded[at - 1] ?? '')) continue;
    let same = true;
    for (let step = 0; step < needle.length; step += 1) {
      if (folded[at + step] !== needle[step]) {
        same = false;
        break;
      }
    }
    if (same) hits.push(at);
  }
  return hits;
};

/** What was typed, folded the same way the value is. */
const needleOf = (word: string): string[] => foldWithMap([...word]).folded;

/**
 * One value, split into the runs to light and the runs to leave.
 *
 * Returns the whole value as a single unlit segment when nothing can be located,
 * which is the graceful case: the row is still correct and only the yellow is
 * missing.
 */
export const segmentsOf = (value: string, typed: string): readonly Segment[] => {
  const words = wordsIn(typed);
  const chars = [...value];
  if (chars.length === 0 || words.length === 0) return [{ text: value, lit: false }];

  const { folded, from, dropped } = foldWithMap(chars);
  const litChar = new Array<boolean>(chars.length).fill(false);
  let any = false;

  for (const word of words) {
    const needle = needleOf(word);
    for (const at of startsAt(folded, needle)) {
      for (let step = 0; step < needle.length; step += 1) {
        const original = from[at + step];
        if (original !== undefined) litChar[original] = true;
      }
      any = true;
    }
  }

  if (!any) return [{ text: value, lit: false }];

  // Close over a dropped character sitting inside a lit run, so the comma of a
  // matched "1,023" is lit with the digits around it. Without this the row
  // draws a lit 1, an unlit comma, and a lit 023.
  for (const at of dropped) {
    if ((litChar[at - 1] ?? false) && (litChar[at + 1] ?? false)) litChar[at] = true;
  }

  // Coalesce the flags into runs, so the row draws a handful of spans and not
  // one per character.
  const segments: Segment[] = [];
  let run = chars[0] ?? '';
  let lit = litChar[0] ?? false;
  for (let at = 1; at < chars.length; at += 1) {
    const isLit = litChar[at] ?? false;
    if (isLit === lit) {
      run += chars[at] ?? '';
      continue;
    }
    segments.push({ text: run, lit });
    run = chars[at] ?? '';
    lit = isLit;
  }
  segments.push({ text: run, lit });
  return segments;
};

/** Does what was typed start a term in these words? */
export const landsIn = (words: string, typed: string): boolean => {
  if (words === '') return false;
  const { folded } = foldWithMap([...words]);
  for (const word of wordsIn(typed)) {
    if (startsAt(folded, needleOf(word)).length > 0) return true;
  }
  return false;
};

/**
 * Which field this row matched in, or null when nothing can be attributed.
 *
 * The order is deliberate and it is: the row's OWN words first, then what it
 * points at.
 *
 * A sale can hold "ramesh" in its note AND point at Ramesh. Saying "Note ·
 * ramesh will collect" is then the more honest answer, because those words are
 * the row's own and a person reading the row can see them. The reference is the
 * answer only when the row itself says nothing.
 *
 * `seeds` holds the ids of the entries whose own names matched, which is what
 * the reference step already found. Without it a reference match cannot be told
 * from a field that merely holds an id.
 */
export const whereMatched = (
  schema: RecordSchemaResponse,
  values: EntryValues,
  typed: string,
  seeds: ReadonlySet<string>,
): MatchWhere | null => {
  const name = ownNameField(schema, values);
  if (name !== null && landsIn(fieldWords(schema, name, values), typed)) {
    return { kind: 'title' };
  }

  const byOrd = [...schema.fields].sort((left, right) => left.ord - right.ord);

  for (const field of byOrd) {
    if (name !== null && field.id === name.id) continue;
    if (POINTS_AT_AN_ENTRY.has(field.type)) continue;
    if (landsIn(fieldWords(schema, field, values), typed)) {
      return { kind: 'field', field };
    }
  }

  for (const field of byOrd) {
    if (!POINTS_AT_AN_ENTRY.has(field.type)) continue;
    const value = values[field.id];
    if (typeof value === 'string' && seeds.has(value)) {
      return { kind: 'reference', field, targetId: value };
    }
    if (Array.isArray(value)) {
      for (const one of value) {
        if (typeof one === 'string' && seeds.has(one)) {
          return { kind: 'reference', field, targetId: one };
        }
      }
    }
  }

  return null;
};

/**
 * Should this row draw a field label at all?
 *
 * No for a title match, and no when nothing could be attributed — which is the
 * near-miss case, where no term matched and a label would be a guess.
 */
export const showsLabel = (where: MatchWhere | null): boolean =>
  where !== null && where.kind !== 'title';
